/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import clsx from "clsx";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import updateLocale from "dayjs/plugin/updateLocale";
import weekday from "dayjs/plugin/weekday";
import React from "react";
import Calendar from "./";
import { SlotTime } from "./availabilities";
import CalendarHeader from "./calendar-header";
import CalendarToolbar from "./calendar-toolbar";
import TimeSlotWrapper from "./time-slot-wrapper";
dayjs.extend(timezone);

// Dynamic imports for react-big-calendar types
type View = "month" | "week" | "work_week" | "day" | "agenda";
type ViewsProps = Record<string, React.ComponentType<any>>;
type ViewStatic = {
  range?: (date: Date, opts: any) => Date[];
  navigate?: (date: Date, action: string, opts: any) => Date;
  title?: (date: Date) => string;
};

// TypeScript types for props
export interface BigCalendarProps {
  CustomEvent?: React.ComponentType<{
    className: string;
    title: string;
  }>;
  CustomSlotComponent?: React.ComponentType<{
    className: string;
    slotValue: Date | string;
  }>;
  classes?: Record<string, string>;
  slotDuration?: number;
  slotCount?: number;
  defaultDate?: Date;
  defaultView?: View | "customWeek";
  endTime?: {
    hour: number;
    minutes: number;
  };
  timeLabelTextFunc?: (timeLabelText: string) => string;
  onSelectEvent?: (event: SlotTime) => void;
  onSelectSlot?: (slot: {
    start: Date;
    end: Date;
  }) => void;
  selectable?: boolean;
  startTime?: {
    hour: number;
    minutes: number;
  };
  events?: SlotTime[];
  noSelectionOfPastDate?: boolean;
  views?: ViewsProps | Record<string, React.ComponentType<any> & ViewStatic>;
  viewCounts?: number;
  selected?: Array<{
    end: string;
    start: string;
    unitId?: string;
  }> | SlotTime[];
  ContainerProps?: React.HTMLAttributes<HTMLDivElement>;
  ToolbarComponent?: React.ComponentType<any>;
  toolbarProps?: Record<string, unknown>;
  [key: string]: unknown;
  startWeekDay?: number;
}

// Dynamic localizer initialization - removed from top level

const classes = {
  calendar: "tw-calendar",
  timeCellWrapper: "tw-timeCellWrapper",
  eventWrapper: "tw-eventWrapper",
  eventComp: "tw-eventComp",
  slotComp: "tw-slotComp"
};

// Tailwind styles for the calendar container and elements
const calendarContainerClass = "pt-4 flex flex-col w-full max-w-screen-xl mx-auto";
const eventWrapperClass = "absolute mt-0.5 min-h-[25px] ml-2.5 overflow-hidden flex justify-center";
const eventCompClass = "truncate overflow-hidden whitespace-nowrap max-w-full max-h-full block px-1";
const slotCompClass = "truncate overflow-hidden whitespace-nowrap max-w-full h-full block p-0 text-center leading-6";
const timeGutterHeaderClass = "min-w-[63px]";
const MOBILE_LONG_PRESS_THRESHOLD_MS = 1; // Improve mobile long-press responsiveness for slot selection

const BigCalendar: React.FC<BigCalendarProps> = ({
  CustomEvent,
  CustomSlotComponent,
  classes: classList,
  slotDuration,
  slotCount,
  defaultDate,
  defaultView,
  endTime,
  timeLabelTextFunc,
  onSelectEvent,
  onSelectSlot,
  selectable,
  startTime,
  events,
  noSelectionOfPastDate,
  views,
  viewCounts,
  selected,
  ContainerProps,
  ToolbarComponent = function Toolbar(p: any) {
    return <CalendarToolbar {...p} viewCounts={viewCounts} {...p.toolbarProps || {}} />;
  },
  startWeekDay = defaultDate ? defaultDate.getDay() : 0,
  ...props
}) => {
  // Dynamic localizer initialization
  const [localizer, setLocalizer] = React.useState<any>(null);
  React.useEffect(() => {
    let mounted = true;
    const initializeLocalizer = async () => {
      // Configure dayjs BEFORE creating the localizer
      if (typeof startWeekDay === "number") {
        dayjs.extend(weekday);
        dayjs.extend(updateLocale);
        // Update the locale to use your custom week start
        dayjs.updateLocale("en", {
          weekStart: startWeekDay
        });
      }
      try {
        const {
          dayjsLocalizer
        } = await import("react-big-calendar");
        if (mounted && typeof window !== "undefined") {
          setLocalizer(dayjsLocalizer(dayjs));
        }
      } catch (_e) {
        // ignore
      }
    };
    initializeLocalizer();
    return () => {
      mounted = false;
    };
  }, [startWeekDay]);
  if (!localizer) {
    return <div>Loading calendar...</div>;
  }
  return <>
			<div className={calendarContainerClass} {...ContainerProps}>
				<Calendar DateHeader={CalendarHeader} EventWrapper={({
        style: {
          top,
          height,
          width
        },
        label,
        event,
        ...eventProps
      }: any) => <div className={clsx(eventWrapperClass, classList?.event)} style={{
        top: `${top}%`,
        height: `${height + 17}px`,
        width: `${width}%`
      }} title={`${label}: ${event.title || ""}`}>
							{CustomEvent ? <CustomEvent className={eventCompClass} title={event.title || ""} {...eventProps} /> : <button className={eventCompClass + " w-full bg-primary text-white rounded"}>
									{event.title || ""}
								</button>}
						</div>} TimeHeader={() => <div className={timeGutterHeaderClass} />} TimeSlotWrapper={(slotProps: any) => <TimeSlotWrapper timeLabelTextFunc={timeLabelTextFunc} CustomSlotComponent={CustomSlotComponent} slotClasses={{
        root: clsx(classList?.slot, slotCompClass)
      }} {...slotProps} />} Toolbar={ToolbarComponent} classes={classes} defaultDate={defaultDate} defaultView={defaultView} endTime={endTime} events={events} localizer={localizer} noSelectionOfPastDate={noSelectionOfPastDate} onSelectEvent={onSelectEvent} onSelectSlot={onSelectSlot} selectable={selectable} startTime={startTime} step={slotDuration} timeSlots={slotCount} views={views} {...onSelectEvent ? {
        selected
      } : {
        defaultSelected: selected
      }} {...viewCounts ? {
        viewCounts
      } : {}} longPressThreshold={MOBILE_LONG_PRESS_THRESHOLD_MS} {...props} />
			</div>
		</>;
};
export default BigCalendar;